import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import {
  Container,
  Grid,
  Paper,
  Card,
  Typography,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
  CircularProgress,
  TablePagination,
  Tooltip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Folder,
  Search,
  Refresh,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { sendDataWithPost } from '../../../services/services';
import { showSuccessMsg, showErrorMsg } from '../../../utils/toast';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import CustomDatePicker from '../../../components/form/CustomDatePicker';
import { acquirerList } from '../../../utils/constants';
import { COLORS } from '../../../utils/colors';
import { GREY } from '../../../theme/palette';
import palette from '../../../theme/palette';
import { StorageUtils } from '../../../utils';
import { APP_USER_SESSION } from '../../../constant/AppConstants';
import CircularLoader from '../../../components/circularLoader';
import ButtonCustom from '../../../components/ButtonCustom';
import { styles } from '../../../utils/styles';

const UploadDialog = ({ open, onClose, onFileUploaded, userRole }) => {
  const fileInputRef = useRef(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedAcquirerData, setSelectedAcquirerData] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleAcquirerChange = (event, newValue) => {
    setSelectedAcquirerData(newValue);
  };

  const handleUploadFile = async () => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('acquirer', selectedAcquirerData.id);

    setUploadLoading(true);

    const handleSuccess = (response) => {
      showSuccessMsg(response.data.detail);
      setSelectedFile(null);
      setSelectedAcquirerData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      onFileUploaded();
      setUploadLoading(false);
      onClose();
      // Trigger refresh of acquirer files list
      window.dispatchEvent(new CustomEvent('refreshAcquirerFiles', { 
        detail: { acquirer: selectedAcquirerData.id } 
      }));
    };

    try {
      const response = await api.post('/payout/ingestor/ingest_upload_v2/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleSuccess(response);
    } catch (error) {
      // Timeout error does not have response
      if (!error.response) {
        showSuccessMsg('File uploaded and in progress');
        setSelectedFile(null);
        setSelectedAcquirerData(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        onFileUploaded();
        onClose();
        window.dispatchEvent(new CustomEvent('refreshAcquirerFiles', {
          detail: { acquirer: selectedAcquirerData?.id }
        }));
      } else {
        showErrorMsg(error.response?.data?.detail || error.message);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleClose = () => {
    if (!uploadLoading) {
      setSelectedFile(null);
      setSelectedAcquirerData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Upload Acquirer File
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        <FormControl fullWidth sx={{ mb: 3, mt: 2 }}>
          <Autocomplete
            options={(() => {
              const shopeeIndex = acquirerList.findIndex(item => item.id === "shopee");
              return [
                ...acquirerList.slice(0, shopeeIndex + 1),
                { id: "shopee_th", name: "ShopeePay TH" },
                ...acquirerList.slice(shopeeIndex + 1)
              ].map((item) => ({
                id: item.id,
                label: item.id === "shopee" ? "ShopeePay SG" : item.name,
              }));
            })()}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Folder sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  {option.label}
                </Box>
              </li>
            )}
            onChange={handleAcquirerChange}
            value={selectedAcquirerData}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Acquirer"
                variant="outlined"
                fullWidth
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.id === value?.id
            }
          />
        </FormControl>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: selectedFile ? 'success.main' : 'grey.300',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              backgroundColor: selectedFile ? 'success.light' : 'grey.50',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: selectedFile ? 'success.dark' : 'primary.main',
                backgroundColor: selectedFile ? 'success.light' : 'primary.50',
              }
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            {selectedFile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  mb: 1
                }}>
                  <Description sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="body1" color="success.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  mb: 1
                }}>
                  <CloudUpload sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Choose a file
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Supported formats: XLSX, XLS
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}>
                  <CloudUpload sx={{ fontSize: 16 }} />
                  Browse Files
                </Box>
              </Box>
            )}
          </Box>
          
          {selectedFile && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              borderRadius: 1, 
              backgroundColor: 'success.light',
              border: '1px solid',
              borderColor: 'success.main'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: 'success.main',
                  mr: 1
                }} />
                <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                  File validation passed
                </Typography>
              </Box>
              <Typography variant="caption" color="success.dark">
                File type and size are valid for upload
              </Typography>
            </Box>
          )}
        </Box>

        {uploadLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Uploading file...
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={uploadLoading}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={uploadLoading}
          disabled={!selectedFile || !selectedAcquirerData}
          onClick={handleUploadFile}
          sx={{ 
            backgroundColor: COLORS.OXYGEN7_PURPLE,
            '&:hover': {
              backgroundColor: 'rgba(91, 45, 130, 0.8)'
            }
          }}
          startIcon={<CloudUpload />}
        >
          Upload Acquirer File
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const FilesListTable = ({ files, loading, onSort, sortBy, sortOrder }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [fileToDownload, setFileToDownload] = useState(null);

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const handleFileNameClick = (file) => {
    setFileToDownload(file);
    setOpenDownloadDialog(true);
  };

  const handleDownloadConfirm = () => {
    if (fileToDownload) {
      downloadFile(fileToDownload);
    }
    setOpenDownloadDialog(false);
    setFileToDownload(null);
  };

  const handleDownloadCancel = () => {
    setOpenDownloadDialog(false);
    setFileToDownload(null);
  };

  const downloadFile = async (file) => {
    try {
      console.log('file', file);
      
      // Debug: Log the entire file object
      console.log('Download file object:', file);
      
      // Validate required fields
      if (!file.file_name) {
        console.error('File name is missing from file object:', file);
        showErrorMsg('File name is missing');
        return;
      }
      
      // Use FullPath directly from backend response
      const fullPath = file.FullPath;
      
      if (!fullPath) {
        console.error('FullPath is missing from file object:', file);
        showErrorMsg('FullPath is missing from file data');
        return;
      }
      
      console.log('Attempting download:', { 
        fileName: file.file_name,
        fullPath: fullPath,
        acquirer: file.acquirer
      });
      
      // Use POST method only (consistent with other download endpoints)
      const response = await api.post('/payout/ingestor/download_file/', {
        full_path: fullPath,
        file_name: file.file_name
      }, {
        responseType: 'blob'  // Important: Tell axios to expect binary data
      });

      console.log('API Response status:', response.status);

      if (response.status === 200) {
        const blob = response.data;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Use filename from backend response or original name
        let filename = file.file_name;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        showSuccessMsg(`Download completed: ${file.file_name}`);
      } else {
        console.error('Download failed:', response.status, response.statusText);
        if (response.status === 401) {
          showErrorMsg('Authorization failed. Please login again.');
        } else if (response.status === 404) {
          showErrorMsg('File not found or download endpoint not available.');
        } else if (response.status === 403) {
          showErrorMsg('Access denied. You do not have permission to download this file.');
        } else {
          showErrorMsg(`Download failed: ${response.status} ${response.statusText}`);
        }
      }
      
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 401) {
        showErrorMsg('Authorization failed. Please login again.');
      } else if (error.response?.status === 404) {
        showErrorMsg('File not found or download endpoint not available.');
      } else if (error.response?.status === 403) {
        showErrorMsg('Access denied. You do not have permission to download this file.');
      } else {
        showErrorMsg(`Download failed: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_process':
        return palette.warning.main;
      case 'processed':
        return palette.success.main;
      case 'failed':
        return palette.error.main;
      default:
        return GREY[500];
    }
  };

  const paginatedFiles = files.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  cursor: 'pointer', 
                  userSelect: 'none',
                  minWidth: 400,
                  width: '30%'
                }}
                onClick={() => onSort('FileName')}
              >
                File Name {getSortIcon('FileName')}
              </TableCell>
              <TableCell 
                sx={{ 
                  cursor: 'pointer', 
                  userSelect: 'none',
                  minWidth: 100
                }}
                onClick={() => onSort('acquirer')}
              >
                Acquirer {getSortIcon('acquirer')}
              </TableCell>
              <TableCell 
                sx={{ 
                  cursor: 'pointer', 
                  userSelect: 'none',
                  minWidth: 80
                }}
                onClick={() => onSort('Size')}
              >
                Size {getSortIcon('Size')}
              </TableCell>
              <TableCell 
                sx={{ 
                  cursor: 'pointer', 
                  userSelect: 'none',
                  minWidth: 150
                }}
                onClick={() => onSort('LastModified')}
              >
                Upload Date {getSortIcon('LastModified')}
              </TableCell>
              <TableCell 
                sx={{ minWidth: 100 }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography
                    gutterBottom
                    align="center"
                    variant="subtitle1"
                  >
                    Not found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedFiles.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>
                    <Tooltip title={file.file_name}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#1b6ac9',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          '&:hover': {
                            color: '#1976d2',
                            textDecoration: 'underline'
                          },
                          wordBreak: 'break-word',
                          lineHeight: 1.4
                        }}
                        onClick={() => handleFileNameClick(file)}
                      >
                        {file.file_name}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{file.acquirer}</TableCell>
                  <TableCell>{file.file_size_formatted}</TableCell>
                  <TableCell>{file.upload_date_formatted}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: getStatusColor(file.status),
                        fontWeight: 500
                      }}
                    >
                      {file.status_display}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={files.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />


      <Dialog
        open={openDownloadDialog}
        onClose={handleDownloadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Download Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Do you want to download{' '}
            <Typography 
              component="span" 
              sx={{ 
                fontWeight: 600, 
                color: '#1b6ac9',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              {fileToDownload?.file_name}
            </Typography>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDownloadCancel} 
            variant="outlined"
            sx={{ mr: 1 }}
          >
            No
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained"
            sx={{ 
              backgroundColor: COLORS.OXYGEN7_PURPLE,
              '&:hover': {
                backgroundColor: 'rgba(91, 45, 130, 0.8)'
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AcquirerFiles = () => {
  const [uploadDate, setUploadDate] = useState(new Date());
  const [selectedAcquirer, setSelectedAcquirer] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acquirers, setAcquirers] = useState([]);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  
  // Get user permissions
  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION).role;
  const acquirerFileRole = userRole.acquirer_file || [];
  
  const [sortBy, setSortBy] = useState('LastModified');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadAcquirers();
  }, []);

  useEffect(() => {
    loadFiles();
  }, [sortBy, sortOrder]);

  const handleSearch = () => {
    loadFiles();
  };

  const handleReset = () => {
    setUploadDate(new Date());
    setSelectedAcquirer('all');
    setSelectedStatus('all');
  };

  const loadAcquirers = async () => {
    try {
      // Use shared acquirer constants (same as Finance -> Import Data)
      const shopeeIndex = acquirerList.findIndex(item => item.id === "shopee");
      const acquirerListWithShopeeTh = [
        ...acquirerList.slice(0, shopeeIndex + 1),
        { id: "shopee_th", name: "ShopeePay TH" },
        ...acquirerList.slice(shopeeIndex + 1)
      ];
      setAcquirers(acquirerListWithShopeeTh.map(item => ({ 
        id: item.id, 
        name: item.id === "shopee" ? "ShopeePay SG" : item.name 
      })));
    } catch (error) {
      console.error('Failed to load acquirers:', error);
    }
  };

  const loadFiles = async () => {
    setLoading(true);
    
    // Prepare request data with sorting parameters
    const requestData = {
      acquirer: selectedAcquirer,
      upload_date: moment(uploadDate).format('YYYY-MM-DD'),
      status: selectedStatus === 'all' ? null : selectedStatus, // Send null for 'all' status
      sort_by: sortBy,
      sort_order: sortOrder
    };
    
    // Use callback pattern for sendDataWithPost
    const handleSuccess = (response) => {
      const allFiles = [];

      console.log('response', response);
      
      // Handle merged response with status field
      if (response.data?.result) {
        response.data.result.forEach((file, index) => {
          allFiles.push({
            id: index + 1,
            file_name: file.FileName,
            file_size_formatted: file.Size,
            upload_date_formatted: moment.parseZone(file.LastModified).format('YYYY-MM-DD HH:mm:ss'),
            status: file.status === 'Processed' ? 'processed' : 'in_process',
            status_display: file.status === 'Processed' ? 'Processed' : 'In process',
            acquirer: file.acquirer || selectedAcquirer.toUpperCase(),
            FullPath: file.FullPath
          });
        });
      }
      
      setFiles(allFiles);
      setLoading(false);
    };

    const handleError = () => {
      showErrorMsg('Failed to load files');
      setLoading(false);
    };

    sendDataWithPost(
      '/payout/ingestor/files_uploaded_v2/',
      requestData,
      null,
      null,
      handleSuccess,
      handleError
    );
  };

  const handleFileUploaded = () => {
    loadFiles();
  };

  // Handle column sorting with proper defaults
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      
      if (column === 'LastModified') {
        setSortOrder('desc');
      } else if (column === 'Size') {
        setSortOrder('desc');
      } else {
        setSortOrder('asc');
      }
    }
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Acquirer Files" isShowLink={false} />
      
      <Card sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Upload Date
              </Typography>
            <CustomDatePicker
              value={uploadDate}
              onChange={(newValue) => setUploadDate(newValue)}
              fullWidth
              size="small"
              views={["year", "month", "day"]}
              openTo="day"
              sx={{ 
                '& .MuiOutlinedInput-root': {
                    height: "30px",
                    fontSize: "14px"
                }
              }}
            />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Acquirer
              </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedAcquirer}
                onChange={(e) => setSelectedAcquirer(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
              >
                <MenuItem value="all">All Acquirers</MenuItem>
                {acquirers.map((acquirer) => (
                  <MenuItem key={acquirer.id} value={acquirer.id}>
                    {acquirer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Status
              </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="in_process">In Process</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
              </Select>
            </FormControl>
            </Box>
          </Grid>
          {acquirerFileRole.includes('Upload file') && (
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                <LoadingButton
                  variant="contained"
                  size="small"
                  sx={{ 
                    backgroundColor: COLORS.OXYGEN7_PURPLE,
                    '&:hover': {
                      backgroundColor: 'rgba(91, 45, 130, 0.8)'
                    }
                  }}
                  onClick={() => setOpenUploadDialog(true)}
                >
                  Upload
                </LoadingButton>
              </Box>
            </Grid>
          )}
        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <ButtonCustom
            sx={{ ml: 1, mt: 2, ...styles.customButton }}
            icon={<Search />}
            variant="contained"
            title="Search"
            onClick={handleSearch}
          />
          <ButtonCustom
            sx={{ ml: 1, mt: 2 }}
            icon={<Refresh />}
            title="Reset"
            onClick={handleReset}
          />
        </Stack>
      </Card>

      {loading ? (
        <CircularLoader />
      ) : (
        <Card sx={{ borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <FilesListTable 
            files={files} 
            loading={false}
            onRefresh={loadFiles}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        </Card>
      )}

      {/* Upload Dialog */}
      <UploadDialog 
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        onFileUploaded={handleFileUploaded}
        userRole={userRole}
      />
    </Container>
  );
};

export default AcquirerFiles;
